import { concat, from, of } from 'rxjs';
import { EnrollTwoFactorRequest, Error as MultipassError } from '@square/dashboard-protos/models/squareup/multipass/external';
import { SmsTwoFactor, TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';
import { ActionType, setOnboardingScreenAction } from './actions';
import { INVEST_TWO_FACTOR } from '@banking/ui/utils/invest-cdp-events';

// Action
// =============================================================================

// Action Creators
// =============================================================================

export const userChangePhoneNumberAction = phoneNumber => ({
  type: ActionType.UserChangePhoneNumber,
  phoneNumber
});
export const userChangeVerificationCodeAction = verificationCode => ({
  type: ActionType.UserChangeVerificationCode,
  verificationCode
});
export const userClickEnrollAction = () => ({
  type: ActionType.UserClickEnroll
});
export const userClickVerifyAction = () => ({
  type: ActionType.UserClickVerify
});
export const userClickResendCodeAction = () => ({
  type: ActionType.UserClickResendCode
});
export const phoneNumberEnrollTwoFactorReceivedAction = response => ({
  type: ActionType.PhoneNumberEnrollTwoFactorReceived,
  response
});
export const phoneNumberEnrollTwoFactorErrorAction = () => ({
  type: ActionType.PhoneNumberEnrollTwoFactorError
});
export const verifyCodeEnrollTwoFactorReceivedAction = response => ({
  type: ActionType.VerifyCodeEnrollTwoFactorReceived,
  response
});
export const verifyCodeEnrollTwoFactorErrorAction = () => ({
  type: ActionType.VerifyCodeEnrollTwoFactorError
});
export const toggleButtonLoadingAction = value => ({
  type: ActionType.ToggleButtonLoading,
  value
});

// Misc
// =============================================================================
const createEnrollTwoFactorResponse = (multipass, idempotenceKey, phone, verificationCode) => {
  const twoFactorDetails = TwoFactorDetails.create({
    sms: SmsTwoFactor.create({
      phone,
      verificationCode
    })
  });
  return multipass.enrollTwoFactor(EnrollTwoFactorRequest.create({
    twoFactorDetails,
    idempotenceKey
  }));
};

// Update
// =============================================================================

export function handleUserChangePhoneNumber(state, action) {
  const {
    phoneNumber
  } = action;
  return [{
    ...state,
    phoneNumber,
    phoneNumberError: ''
  }, null];
}
export function handleUserChangeVerificationCode(state, action) {
  const {
    verificationCode
  } = action;
  return [{
    ...state,
    verificationCode,
    verificationCodeError: ''
  }, null];
}
export function handleUserClickEnroll(state, context) {
  if (!state.phoneNumber) {
    const phoneNumberError = context.intl.t('banking.checking.onboarding.twoFactor.phoneNumber.validation.invalidNumber');
    return [{
      ...state,
      phoneNumberError
    }, null];
  }
  const idempotenceKey = context.getUUIDString();
  const work = () => {
    const enrollTwoFactorResponse = createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber).then(phoneNumberEnrollTwoFactorReceivedAction, phoneNumberEnrollTwoFactorErrorAction);
    return concat(of(toggleButtonLoadingAction(true)), from(enrollTwoFactorResponse));
  };
  return [{
    ...state,
    phoneNumberError: ''
  }, work];
}
export function handleUserClickVerify(state, context) {
  if (!state.verificationCode || state.verificationCode.length < 6) {
    const verificationCodeError = context.intl.t('banking.checking.onboarding.twoFactor.verifyCode.validation.invalidCode');
    return [{
      ...state,
      verificationCodeError
    }, null];
  }
  const idempotenceKey = context.getUUIDString();
  const work = () => {
    const enrollTwoFactorResponse = createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber, state.verificationCode).then(verifyCodeEnrollTwoFactorReceivedAction, verifyCodeEnrollTwoFactorErrorAction);
    return concat(of(toggleButtonLoadingAction(true)), from(enrollTwoFactorResponse));
  };
  return [{
    ...state,
    verificationCodeError: ''
  }, work];
}
export function handleUserClickResendCode(state, context) {
  const idempotenceKey = context.getUUIDString();
  createEnrollTwoFactorResponse(context.multipass, idempotenceKey, state.phoneNumber);
  return [state, null];
}
export function handlePhoneNumberEnrollTwoFactorReceived(state, action, context) {
  const {
    response
  } = action;
  const {
    error
  } = response;
  if (error) {
    const phoneNumberError = error === MultipassError.INVALID_ALIAS ? context.intl.t('banking.checking.onboarding.twoFactor.phoneNumber.validation.invalidNumber') : context.intl.t('banking.checking.onboarding.twoFactor.error.server');
    const nextState = {
      ...state,
      phoneNumberError
    };
    return [nextState, () => toggleButtonLoadingAction(false)];
  }
  const work = () => {
    return concat(of(toggleButtonLoadingAction(false)), of(setOnboardingScreenAction('verifyCode')));
  };
  return [{
    ...state,
    verificationCodeError: ''
  }, work];
}
export function handlePhoneNumberEnrollTwoFactorError(state, context) {
  const nextState = {
    ...state,
    phoneNumberError: context.intl.t('banking.checking.onboarding.twoFactor.error.server')
  };
  return [nextState, () => toggleButtonLoadingAction(false)];
}
export function handleVerifyCodeEnrollTwoFactorReceived(state, action, context) {
  const {
    response
  } = action;
  const {
    error,
    complete
  } = response;
  if (error) {
    const verificationCodeError = error === MultipassError.INVALID_CREDENTIALS ? context.intl.t('banking.checking.onboarding.twoFactor.verifyCode.validation.invalidCode') : context.intl.t('banking.checking.onboarding.twoFactor.error.server');
    const nextState = {
      ...state,
      verificationCodeError
    };
    context.dataApi.track(INVEST_TWO_FACTOR, {
      twoFactorStatus: 'error',
      token: context.capitalEngineContext.sessionContext.merchantToken
    });
    return [nextState, () => toggleButtonLoadingAction(false)];
  }
  const isTwoFactorEnabled = complete;
  context.dataApi.track(INVEST_TWO_FACTOR, {
    twoFactorStatus: complete,
    token: context.capitalEngineContext.sessionContext.merchantToken
  });
  return [{
    ...state,
    isTwoFactorEnabled
  }, () => toggleButtonLoadingAction(false)];
}
export function handleVerifyCodeEnrollTwoFactorError(state, context) {
  const nextState = {
    ...state,
    verificationCodeError: context.intl.t('banking.checking.onboarding.twoFactor.error.server')
  };
  return [nextState, () => toggleButtonLoadingAction(false)];
}
export function handleToggleButtonLoading(state, action) {
  const toggleButtonLoading = action.value ?? !state.toggleButtonLoading;
  return [{
    ...state,
    toggleButtonLoading
  }, null];
}